import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { siteSettingsApi, UpdateSiteSettingRequest } from '../services/api';
import toast from 'react-hot-toast';
import AdminEmailTemplates from './AdminEmailTemplates';
import ConfirmationModal from './ConfirmationModal';
import { useConfirmationModal, modalPresets } from '../hooks/useConfirmationModal';

type SectionType = 'general' | 'urls' | 'seo' | 'contact' | 'social' | 'system' | 'analytics' | 'legal' | 'features' | 'email' | 'email-templates';

const AdminSiteSettings: React.FC = () => {
  const [activeSection, setActiveSection] = useState<SectionType>('general');
  const [formData, setFormData] = useState<UpdateSiteSettingRequest>({
    frontendUrl: '',
    backendUrl: '',
    siteTitle: '',
    siteDescription: '',
    businessName: '',
    contactEmail: '',
    siteAuthor: '',
    defaultLanguage: 'fa',
    copyrightText: '',
    maintenanceMode: false,
    siteActive: true,
    maxFileUploadSizeMB: 10,
    allowedFileTypes: 'jpg,jpeg,png,gif,pdf,doc,docx',
    defaultCurrency: 'IRR',
    timezone: 'Asia/Tehran',
    enableRegistration: true,
    enableGuestPosting: false,
    enableMessaging: true,
    enableReviews: false,
    enableOtpOnlyLogin: true,
  });

  const queryClient = useQueryClient();

  // Confirmation modal hook
  const { isOpen, modalConfig, showModal, confirm, cancel } = useConfirmationModal();

  // Fetch current settings
  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: async () => {
      const response = await siteSettingsApi.get();
      return response.data;
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateSiteSettingRequest) => siteSettingsApi.update(data),
    onSuccess: () => {
      toast.success('Settings updated successfully!', {
        style: {
          fontFamily: 'Arial, sans-serif',
          textAlign: 'left',
          direction: 'ltr'
        }
      });
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      console.error('Error updating settings:', error);
      toast.error('Error updating settings', {
        style: {
          fontFamily: 'Arial, sans-serif',
          textAlign: 'left',
          direction: 'ltr'
        }
      });
    },
  });

  // Initialize mutation
  const initializeMutation = useMutation({
    mutationFn: () => siteSettingsApi.initialize(),
    onSuccess: () => {
      toast.success('Default settings created successfully!', {
        style: {
          fontFamily: 'Arial, sans-serif',
          textAlign: 'left',
          direction: 'ltr'
        }
      });
      queryClient.invalidateQueries({ queryKey: ['siteSettings'] });
    },
    onError: (error: any) => {
      console.error('Error initializing settings:', error);
      toast.error('Error creating default settings', {
        style: {
          fontFamily: 'Arial, sans-serif',
          textAlign: 'left',
          direction: 'ltr'
        }
      });
    },
  });

  // Update form data when settings are loaded
  useEffect(() => {
    if (settings) {
      setFormData({
        frontendUrl: settings.frontendUrl,
        backendUrl: settings.backendUrl,
        apiBaseUrl: settings.apiBaseUrl,
        cdnUrl: settings.cdnUrl,
        siteTitle: settings.siteTitle,
        siteDescription: settings.siteDescription,
        metaKeywords: settings.metaKeywords,
        siteLogoUrl: settings.siteLogoUrl,
        faviconUrl: settings.faviconUrl,
        openGraphImageUrl: settings.openGraphImageUrl,
        siteAuthor: settings.siteAuthor,
        defaultLanguage: settings.defaultLanguage,
        businessName: settings.businessName,
        contactEmail: settings.contactEmail,
        supportEmail: settings.supportEmail,
        contactPhone: settings.contactPhone,
        businessAddress: settings.businessAddress,
        copyrightText: settings.copyrightText,
        facebookUrl: settings.facebookUrl,
        twitterUrl: settings.twitterUrl,
        instagramUrl: settings.instagramUrl,
        telegramUrl: settings.telegramUrl,
        linkedInUrl: settings.linkedInUrl,
        maintenanceMode: settings.maintenanceMode,
        siteActive: settings.siteActive,
        maxFileUploadSizeMB: settings.maxFileUploadSizeMB,
        allowedFileTypes: settings.allowedFileTypes,
        defaultCurrency: settings.defaultCurrency,
        timezone: settings.timezone,
        googleAnalyticsId: settings.googleAnalyticsId,
        googleTagManagerId: settings.googleTagManagerId,
        facebookPixelId: settings.facebookPixelId,
        termsOfServiceUrl: settings.termsOfServiceUrl,
        privacyPolicyUrl: settings.privacyPolicyUrl,
        cookiePolicyUrl: settings.cookiePolicyUrl,
        enableRegistration: settings.enableRegistration,
        enableGuestPosting: settings.enableGuestPosting,
        enableMessaging: settings.enableMessaging,
        enableReviews: settings.enableReviews,
        enableOtpOnlyLogin: settings.enableOtpOnlyLogin,
      });
    }
  }, [settings]);

  const handleInputChange = (field: keyof UpdateSiteSettingRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleInitialize = () => {
    showModal({
      title: 'Initialize Settings',
      message: 'Do you want to create default settings?',
      confirmText: 'Yes',
      cancelText: 'No',
      type: 'info',
      onConfirm: () => {
        initializeMutation.mutate();
      }
    });
  };

  const sections = [
    { id: 'general', name: 'General', icon: '🏠' },
    { id: 'urls', name: 'URLs', icon: '🔗' },
    { id: 'seo', name: 'SEO & Metadata', icon: '🔍' },
    { id: 'contact', name: 'Contact Info', icon: '📞' },
    { id: 'social', name: 'Social Media', icon: '📱' },
    { id: 'system', name: 'System', icon: '⚙️' },
    { id: 'analytics', name: 'Analytics', icon: '📊' },
    { id: 'legal', name: 'Legal', icon: '📋' },
    { id: 'features', name: 'Features', icon: '🎯' },
    { id: 'email', name: 'Email', icon: '✉️' },
    { id: 'email-templates', name: 'Email Templates', icon: '📧' },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Settings not found</h3>
        <p className="text-gray-500 mb-6">
          You can create default settings for the first time.
        </p>
        <button
          onClick={handleInitialize}
          disabled={initializeMutation.isPending}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {initializeMutation.isPending ? 'Creating...' : 'Create Default Settings'}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" dir="ltr">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Site Settings</h2>
            <p className="text-blue-100 text-sm">Manage system-wide settings</p>
          </div>
          <div className="text-3xl text-white">⚙️</div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-gray-50 border-r border-gray-200">
          <div className="p-4">
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id as SectionType)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="mr-3 text-lg">{section.icon}</span>
                  {section.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          {activeSection === 'email-templates' ? (
            <AdminEmailTemplates />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* General Settings */}
            {activeSection === 'general' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">General Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Site Title
                    </label>
                    <input
                      type="text"
                      value={formData.siteTitle}
                      onChange={(e) => handleInputChange('siteTitle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Name
                    </label>
                    <input
                      type="text"
                      value={formData.businessName}
                      onChange={(e) => handleInputChange('businessName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site Description
                  </label>
                  <textarea
                    value={formData.siteDescription}
                    onChange={(e) => handleInputChange('siteDescription', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Site Author
                    </label>
                    <input
                      type="text"
                      value={formData.siteAuthor}
                      onChange={(e) => handleInputChange('siteAuthor', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Language
                    </label>
                    <select
                      value={formData.defaultLanguage}
                      onChange={(e) => handleInputChange('defaultLanguage', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="fa">Persian</option>
                      <option value="en">English</option>
                      <option value="ar">Arabic</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Copyright Text
                  </label>
                  <input
                    type="text"
                    value={formData.copyrightText}
                    onChange={(e) => handleInputChange('copyrightText', e.target.value)}
                    placeholder="© 2024 Company Name. All rights reserved."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* URLs Settings */}
            {activeSection === 'urls' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">URL Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Frontend URL
                    </label>
                    <input
                      type="url"
                      value={formData.frontendUrl}
                      onChange={(e) => handleInputChange('frontendUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Backend URL
                    </label>
                    <input
                      type="url"
                      value={formData.backendUrl}
                      onChange={(e) => handleInputChange('backendUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={formData.apiBaseUrl || ''}
                      onChange={(e) => handleInputChange('apiBaseUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CDN URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={formData.cdnUrl || ''}
                      onChange={(e) => handleInputChange('cdnUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* SEO & Metadata Settings */}
            {activeSection === 'seo' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">SEO & Metadata Settings</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meta Keywords
                  </label>
                  <textarea
                    value={formData.metaKeywords || ''}
                    onChange={(e) => handleInputChange('metaKeywords', e.target.value)}
                    rows={2}
                    placeholder="Separate keywords with commas"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logo URL
                    </label>
                    <input
                      type="url"
                      value={formData.siteLogoUrl || ''}
                      onChange={(e) => handleInputChange('siteLogoUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Favicon URL
                    </label>
                    <input
                      type="url"
                      value={formData.faviconUrl || ''}
                      onChange={(e) => handleInputChange('faviconUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Open Graph Image
                  </label>
                  <input
                    type="url"
                    value={formData.openGraphImageUrl || ''}
                    onChange={(e) => handleInputChange('openGraphImageUrl', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Contact Settings */}
            {activeSection === 'contact' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Support Email
                    </label>
                    <input
                      type="email"
                      value={formData.supportEmail || ''}
                      onChange={(e) => handleInputChange('supportEmail', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone || ''}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Address
                  </label>
                  <textarea
                    value={formData.businessAddress || ''}
                    onChange={(e) => handleInputChange('businessAddress', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Social Media Settings */}
            {activeSection === 'social' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Social Media</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Facebook
                    </label>
                    <input
                      type="url"
                      value={formData.facebookUrl || ''}
                      onChange={(e) => handleInputChange('facebookUrl', e.target.value)}
                      placeholder="https://facebook.com/yourpage"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Twitter
                    </label>
                    <input
                      type="url"
                      value={formData.twitterUrl || ''}
                      onChange={(e) => handleInputChange('twitterUrl', e.target.value)}
                      placeholder="https://twitter.com/yourhandle"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Instagram
                    </label>
                    <input
                      type="url"
                      value={formData.instagramUrl || ''}
                      onChange={(e) => handleInputChange('instagramUrl', e.target.value)}
                      placeholder="https://instagram.com/yourhandle"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telegram
                    </label>
                    <input
                      type="url"
                      value={formData.telegramUrl || ''}
                      onChange={(e) => handleInputChange('telegramUrl', e.target.value)}
                      placeholder="https://t.me/yourchannel"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    LinkedIn
                  </label>
                  <input
                    type="url"
                    value={formData.linkedInUrl || ''}
                    onChange={(e) => handleInputChange('linkedInUrl', e.target.value)}
                    placeholder="https://linkedin.com/company/yourcompany"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* System Settings */}
            {activeSection === 'system' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">System Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max File Size (MB)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.maxFileUploadSizeMB}
                      onChange={(e) => handleInputChange('maxFileUploadSizeMB', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Currency
                    </label>
                    <select
                      value={formData.defaultCurrency}
                      onChange={(e) => handleInputChange('defaultCurrency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="IRR">Iranian Rial</option>
                      <option value="USD">US Dollar</option>
                      <option value="EUR">Euro</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allowed File Types
                  </label>
                  <input
                    type="text"
                    value={formData.allowedFileTypes}
                    onChange={(e) => handleInputChange('allowedFileTypes', e.target.value)}
                    placeholder="jpg,jpeg,png,gif,pdf,doc,docx"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">Separate formats with commas</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timezone
                  </label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => handleInputChange('timezone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Asia/Tehran">Tehran</option>
                    <option value="UTC">UTC</option>
                    <option value="Europe/London">London</option>
                    <option value="America/New_York">New York</option>
                  </select>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="maintenanceMode"
                      checked={formData.maintenanceMode}
                      onChange={(e) => handleInputChange('maintenanceMode', e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="maintenanceMode" className="text-sm font-medium text-gray-700">
                      Maintenance Mode
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="siteActive"
                      checked={formData.siteActive}
                      onChange={(e) => handleInputChange('siteActive', e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="siteActive" className="text-sm font-medium text-gray-700">
                      Site Active
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Settings */}
            {activeSection === 'analytics' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Analytics Settings</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Google Analytics ID
                  </label>
                  <input
                    type="text"
                    value={formData.googleAnalyticsId || ''}
                    onChange={(e) => handleInputChange('googleAnalyticsId', e.target.value)}
                    placeholder="G-XXXXXXXXXX or UA-XXXXXXXX-X"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Google Tag Manager ID
                  </label>
                  <input
                    type="text"
                    value={formData.googleTagManagerId || ''}
                    onChange={(e) => handleInputChange('googleTagManagerId', e.target.value)}
                    placeholder="GTM-XXXXXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Facebook Pixel ID
                  </label>
                  <input
                    type="text"
                    value={formData.facebookPixelId || ''}
                    onChange={(e) => handleInputChange('facebookPixelId', e.target.value)}
                    placeholder="xxxxxxxxxxxxxxx"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Legal Settings */}
            {activeSection === 'legal' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Legal Pages</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Terms of Service URL
                  </label>
                  <input
                    type="url"
                    value={formData.termsOfServiceUrl || ''}
                    onChange={(e) => handleInputChange('termsOfServiceUrl', e.target.value)}
                    placeholder="https://yoursite.com/terms"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Privacy Policy URL
                  </label>
                  <input
                    type="url"
                    value={formData.privacyPolicyUrl || ''}
                    onChange={(e) => handleInputChange('privacyPolicyUrl', e.target.value)}
                    placeholder="https://yoursite.com/privacy"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cookie Policy URL
                  </label>
                  <input
                    type="url"
                    value={formData.cookiePolicyUrl || ''}
                    onChange={(e) => handleInputChange('cookiePolicyUrl', e.target.value)}
                    placeholder="https://yoursite.com/cookies"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Features Settings */}
            {activeSection === 'features' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Site Features</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableRegistration"
                      checked={formData.enableRegistration}
                      onChange={(e) => handleInputChange('enableRegistration', e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableRegistration" className="text-sm font-medium text-gray-700">
                      Enable Registration
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableGuestPosting"
                      checked={formData.enableGuestPosting}
                      onChange={(e) => handleInputChange('enableGuestPosting', e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableGuestPosting" className="text-sm font-medium text-gray-700">
                      Guest Posting Without Registration
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableMessaging"
                      checked={formData.enableMessaging}
                      onChange={(e) => handleInputChange('enableMessaging', e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableMessaging" className="text-sm font-medium text-gray-700">
                      Messaging System
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableReviews"
                      checked={formData.enableReviews}
                      onChange={(e) => handleInputChange('enableReviews', e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableReviews" className="text-sm font-medium text-gray-700">
                      Reviews and Rating System
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableOtpOnlyLogin"
                      checked={formData.enableOtpOnlyLogin}
                      onChange={(e) => handleInputChange('enableOtpOnlyLogin', e.target.checked)}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableOtpOnlyLogin" className="text-sm font-medium text-gray-700">
                      OTP-Only Login (Disable Email/Password Login)
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Email Settings */}
            {activeSection === 'email' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Email Settings</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>SMTP Configuration:</strong> Email settings are now configured in the server configuration files for enhanced security. Contact your system administrator to modify SMTP settings.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setActiveSection('general')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </form>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={confirm}
        onClose={cancel}
        title={modalConfig?.title || ''}
        message={modalConfig?.message || ''}
        confirmText={modalConfig?.confirmText || 'OK'}
        cancelText={modalConfig?.cancelText || 'Cancel'}
        type={modalConfig?.type || 'info'}
      />
    </div>
  );
};

export default AdminSiteSettings; 